/**
 * #2945 — the week sheet, pure. No React, no I/O. Unit-tested in
 * `web/tests/lib/weekSheetModel.test.ts`.
 *
 * The department's roster in the shape the KKH team reads in Excel (the
 * *Weekly Combined Deployment* sheet, which `week_sheet.py` writes as xlsx):
 * ward rows down the left under their cover-group head rows; for each day,
 * each session (AM, PM) split into the team's level columns (REG, MO, HO);
 * then the POST CALL and ON CALL rows. A lead's sheet can add LEAVE and MC
 * rows. The rules follow `fill_sheet` in `week_sheet.py`:
 *
 *   - Level columns are the team's levels (`TeamConfig.tiers`) in setup
 *     order, never hardcoded. A team with no levels gets one column,
 *     "Names". A seated person whose level the team does not list goes to
 *     an "Other" column that shows only when needed.
 *   - Sessions are the sessions the wards run, AM before PM, labelled as
 *     the team labels them (`TeamConfig.sessions`). A full-day seat (FD)
 *     fills every session.
 *   - ON CALL is every `oncall` seat, in its level column under the first
 *     session, with the call post name (R1, R2, R3).
 *   - POST CALL on a day is everyone on call the day before who holds no
 *     ward seat that morning. This is a stand-in: the server's xlsx lists
 *     every call of the day before and skips only the works-next-day pairs
 *     (postings, holidays, the H22 table), which the member read does not
 *     carry. A person who works the next day holds a ward seat that morning,
 *     so the stand-in drops the same people in the usual case. Pass the day
 *     before the first date in the assignments, so the first day's POST
 *     CALL is known.
 *   - A seat whose holder is on MC (`absent === "mc"`, lead read only)
 *     leaves its cell empty and names them on the MC row, as the xlsx does.
 *     A seat marked leave stays in its cell, as in the xlsx. The LEAVE row
 *     comes from `away` only.
 *   - A draft seat (`is_published === false`) shows only when the caller
 *     asks for drafts (the lead read). The member read carries released
 *     seats only, so a member never sees a draft.
 *   - An empty cell renders `EMPTY_CELL` ("—"). It never says why.
 *
 * Names: the short name is the abbreviation when the lead set one, else the
 * initials the roster page's person chips derive (founder Q4 (a), see
 * `shortNames`). A tap shows full names.
 *
 * #3022 — consultants. The department's sheet names the consultants the
 * team works with; the roster reads carry them as `consultants`. They are
 * people, never seats: Tarii does not roster them, and they never enter a
 * cell, a count or POST CALL. Phase 1 keeps the call kinds (the consultant
 * on call and the HD consultant) by date in `callConsultants`. The name is
 * as the sheet writes it; a row linked to the viewer reads "You". Phase 2
 * (S8) keeps the ward rows by station in `wardConsultants`: every consultant
 * of a ward on a date, with the beds as the sheet writes them, in bed
 * number order ("Dr A · beds 1–12", "Dr B · beds 13–24"). A ward row with
 * no station (the import's "Do not show") and the weekend round never show.
 */
import { buildDerivedAbbreviations } from "../abbreviations";
import {
  type SheetStation,
  groupHeadLine,
  isClinicStation,
  isWardStation,
  wardGroups,
} from "./stationGroups";

/** An empty cell. */
export const EMPTY_CELL = "—";
/** The level column of a team with no levels. */
export const NAMES_COLUMN_LABEL = "Names";
/** The column for a seated person whose level the team does not list. */
export const OTHER_LEVEL_LABEL = "Other";
export const NO_LEVEL_KEY = "__names";
export const OTHER_LEVEL_KEY = "__other";

export const POST_CALL_LABEL = "Post call";
export const ON_CALL_LABEL = "On call";
export const CLINIC_LABEL = "Clinic";
export const LEAVE_LABEL = "Leave";
export const MC_LABEL = "MC";

const SLOT_SEP = " · ";
const ON_CALL_STATION = "On-call";

// ─── Inputs ───────────────────────────────────────────────────────────

/** One seat, from the member or the lead range read. */
export interface SheetAssignment {
  member_id: string;
  date: string;
  slot_id: string;
  kind: string;
  /** Lead read only. `false` = a draft the team cannot see yet. */
  is_published?: boolean | null;
  /** Lead read only. The holder is away on this date. */
  absent?: "mc" | "leave" | null;
}

export interface SheetMember {
  id: string;
  display_name: string;
  tier?: string | null;
  abbreviation?: string | null;
}

export interface SheetTier {
  key: string;
  label: string;
}

export interface SheetSessionDef {
  key: string;
  label: string;
}

/** A lead's away dates (leave or MC), inclusive. */
export interface SheetAway {
  memberId: string;
  from: string;
  to: string;
  kind: "leave" | "mc";
}

/** One consultant row of a roster read (`ConsultantCoverRead`, #3022). */
export interface SheetConsultantRow {
  date: string;
  /** `ward`, `on_call`, `high_dependency` or `round`. */
  kind: string;
  /** The label to show: "Consultant on call", "HD consultant (W65)". */
  post: string;
  /** Ward rows only (phase 2). */
  station?: string | null;
  /** Ward rows only (phase 2), as the sheet writes them ("1-12"). */
  beds?: string | null;
  /** The name as the sheet writes it, without its tag. */
  name: string;
  /** A bracketed note from the sheet ("Standby"). */
  tag?: string | null;
  /** The team member the name links to, when it is one. */
  member_id?: string | null;
}

export interface WeekSheetInput {
  /** The dates the sheet shows, in order (a week on the phone). */
  dates: readonly string[];
  /** Seats; may reach back one day before `dates[0]` for POST CALL. */
  assignments: readonly SheetAssignment[];
  members: readonly SheetMember[];
  /** The team config's stations (ward rows, groups, clinics). */
  stations: readonly SheetStation[];
  /** The team config's levels, in setup order. */
  tiers: readonly SheetTier[];
  /** The team config's session labels (optional). */
  sessions?: readonly SheetSessionDef[];
  /** The viewer's own membership ids on this team. */
  ownMemberIds?: Iterable<string>;
  /** Show draft seats (lead read only). Default false. */
  includeDrafts?: boolean;
  /** A lead's LEAVE and MC rows. Omit for a member. */
  away?: readonly SheetAway[] | undefined;
  /** #3022 — the read's consultant rows (absent when it has none). */
  consultants?: readonly SheetConsultantRow[] | null | undefined;
}

// ─── Output ───────────────────────────────────────────────────────────

export interface SheetLevel {
  key: string;
  /** The team's label ("MO", "Medical Officer"). */
  label: string;
  /** The header text: the label, or its short form when long. */
  short: string;
}

export interface SheetSession {
  key: string;
  label: string;
}

export interface SheetName {
  memberId: string;
  fullName: string;
  shortName: string;
  own: boolean;
  draft: boolean;
  /** The call post (R1) on the ON CALL row; null elsewhere. */
  post: string | null;
}

export type SheetRowKind = "ward" | "post_call" | "on_call" | "clinic" | "leave" | "mc";

export interface SheetRow {
  key: string;
  label: string;
  kind: SheetRowKind;
  /** Cells by `cellKey(date, session, level)`. A missing key is empty. */
  cells: Record<string, SheetName[]>;
}

export interface SheetRowGroup {
  key: string;
  /** Cover-group letter; null for the ungrouped tail or a flat team. */
  letter: string | null;
  /** "Group A" / "Stations". */
  title: string;
  /** "Group A · 31 · 55 · 51". */
  headLine: string;
  rows: SheetRow[];
}

/** #3022 — one consultant on a line: a person, never a seat. */
export interface SheetConsultant {
  /** As the sheet writes it. */
  name: string;
  tag: string | null;
  /** Ward rows only, as the sheet writes them ("1-12"). */
  beds: string | null;
  memberId: string | null;
  /** Linked to the viewer: the line reads "You". */
  own: boolean;
}

/** #3022 — the kinds a call line shows, in the order members read them. */
export const CONSULTANT_CALL_KINDS = ["on_call", "high_dependency"] as const;
export type ConsultantCallKind = (typeof CONSULTANT_CALL_KINDS)[number];

/** #3022 — a consultant post on a date and who the sheet names on it. */
export interface SheetConsultantPost {
  kind: ConsultantCallKind;
  /** "Consultant on call", "HD consultant (W65)". */
  post: string;
  /** In the sheet's order. */
  people: SheetConsultant[];
}

export interface WeekSheet {
  dates: string[];
  sessions: SheetSession[];
  levels: SheetLevel[];
  /** True when no ward has a cover group: render no head rows. */
  flat: boolean;
  groups: SheetRowGroup[];
  /** POST CALL, ON CALL, then CLINIC (when the team has a clinic), then a
   *  lead's LEAVE and MC rows (when `away` was given). */
  blockRows: SheetRow[];
  /** True when any shown seat is a draft. */
  hasDrafts: boolean;
  /** #3022 — the on-call and HD consultants by date, call post first. A
   *  date with none is absent. */
  callConsultants: Record<string, SheetConsultantPost[]>;
  /** #3022 S8 — the ward consultants by ward (the station name in lower
   *  case), then by date, in bed number order. Read it through
   *  `wardConsultantsOn`. */
  wardConsultants: ReadonlyMap<string, Readonly<Record<string, SheetConsultant[]>>>;
}

export function cellKey(date: string, session: string, level: string): string {
  return `${date}|${session}|${level}`;
}

export function cellNames(
  row: SheetRow,
  date: string,
  session: string,
  level: string,
): SheetName[] {
  return row.cells[cellKey(date, session, level)] ?? [];
}

/** True when any cell of a row on `date` names the viewer. */
export function rowHasOwn(row: SheetRow, date: string): boolean {
  return Object.entries(row.cells).some(
    ([k, names]) => k.startsWith(`${date}|`) && names.some((n) => n.own),
  );
}

/** The viewer's own name on a member screen, a linked consultant row
 *  included (#3022). */
export const YOU_LABEL = "You";

/** #3022 — the consultant posts on call on a date; [] when none. */
export function consultantsOnCall(sheet: WeekSheet, date: string): SheetConsultantPost[] {
  return sheet.callConsultants[date] ?? [];
}

/** #3022 — "Dr Opal Reyes", "Dr Dev Raj (Standby)", or "You". */
export function consultantText(c: SheetConsultant): string {
  const name = c.own ? YOU_LABEL : c.name;
  return c.tag ? `${name} (${c.tag})` : name;
}

/** #3022 — the people on one post: "Dr Zara Lim, Dr Abel Ng". */
export function consultantNames(post: SheetConsultantPost): string {
  return post.people.map(consultantText).join(", ");
}

/** #3022 S8 — the label of a ward's consultant line. */
export const CONSULTANTS_LABEL = "Consultants";

/** A ward number reads "ward 55" in a sentence; a named station as itself. */
export function placeInSentence(name: string): string {
  return /^\d/.test(name.trim()) ? `ward ${name}` : name;
}

/** #3022 S8 — "Consultants on ward 55"; a named station as itself. */
export function consultantsOnWard(ward: string): string {
  return `${CONSULTANTS_LABEL} on ${placeInSentence(ward)}`;
}

/** #3022 — a sheet's beds text names a bed range when it starts with a
 *  digit ("1-12", "25-34, Ward 85 (1-5)"). Any other text ("HAEM/ONCO" on
 *  wards 75 and 76) is a plain note. */
export function isBedRange(beds: string | null | undefined): boolean {
  return /^\d/.test((beds ?? "").trim());
}

/** #3022 S8 — "beds 1–12" from the sheet's "1-12", the rest as written
 *  ("beds 25–34, Ward 85 (1–5)"); "" when the row has no beds. A text that
 *  is not a bed range reads as written, with no "beds": "HAEM/ONCO". */
export function bedsLabel(beds: string | null | undefined): string {
  const text = (beds ?? "").trim().replace(/(\d)\s*-\s*(?=\d)/g, "$1–");
  if (!text) return "";
  return isBedRange(text) ? `beds ${text}` : text;
}

/** #3022 S8 — one consultant on a ward line: "Dr Rachel Ong · beds 1–12",
 *  "Dr Uri Lane (C-Mentor) · beds 1–12", "You · beds 13–24". */
export function wardConsultantText(c: SheetConsultant): string {
  const beds = bedsLabel(c.beds);
  return beds ? `${consultantText(c)} · ${beds}` : consultantText(c);
}

/** #3022 S8 — the consultants of a ward on a date, in bed number order;
 *  [] when the sheet names none. `ward` is the ward row's label. */
export function wardConsultantsOn(sheet: WeekSheet, ward: string, date: string): SheetConsultant[] {
  return sheet.wardConsultants.get(ward.toLocaleLowerCase())?.[date] ?? [];
}

/** #3022 — one consultant post as a screen line: "Consultant on call" and who. */
export interface ConsultantLine {
  /** "Consultant on call", "HD consultant (W65)". */
  label: string;
  /** "Dr Opal Reyes", "Dr Zara Lim, Dr Abel Ng", or "You". */
  names: string;
  /** The same people one by one; `own` marks the viewer ("You"). */
  people: { text: string; own: boolean }[];
}

/** #3022 — consultant posts as screen lines. */
export function consultantLines(posts: readonly SheetConsultantPost[]): ConsultantLine[] {
  return posts.map((p) => ({
    label: p.post,
    names: consultantNames(p),
    people: p.people.map((c) => ({ text: consultantText(c), own: c.own })),
  }));
}

/** #3022 — the consultant posts on call on a date as screen lines. */
export function consultantLinesOnCall(sheet: WeekSheet, date: string): ConsultantLine[] {
  return consultantLines(consultantsOnCall(sheet, date));
}

// ─── Small helpers ────────────────────────────────────────────────────

/** `2026-10-29` + n days, in UTC. */
export function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** The Monday of the week holding `iso`. */
export function mondayOf(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const back = (d.getUTCDay() + 6) % 7;
  return addDays(iso, -back);
}

/** Monday to Sunday of the week holding `iso`. */
export function weekDates(iso: string): string[] {
  const monday = mondayOf(iso);
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

function splitSlot(slotId: string): { station: string; session: string } {
  const idx = slotId.indexOf(SLOT_SEP);
  if (idx < 0) return { station: slotId, session: "" };
  return { station: slotId.slice(0, idx), session: slotId.slice(idx + SLOT_SEP.length) };
}

function isOnCall(a: SheetAssignment): boolean {
  return a.kind === "oncall" || splitSlot(a.slot_id).station === ON_CALL_STATION;
}

/** "R1" from `On-call · R1`; "" for a bare `On-call`. */
export function callPostOf(slotId: string): string {
  const { station, session } = splitSlot(slotId);
  return station === ON_CALL_STATION ? session : station;
}

/**
 * The header text of a level (design note, "Level columns"): a label of
 * four letters or fewer shows as it is; a longer label of several words
 * shows the first letter of each ("Medical Officer" → "MO"); one long word
 * shows its first three letters ("Registrar" → "Reg").
 */
export function levelShortLabel(label: string): string {
  const trimmed = label.trim();
  if (trimmed.length <= 4) return trimmed;
  const words = trimmed.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  if (words.length > 1)
    return words
      .map((w) => w[0] ?? "")
      .join("")
      .toUpperCase();
  return trimmed.slice(0, 3);
}

// ─── #3078 — the row head column ─────────────────────────────────────

/** A letter's width in em, for the sheet's sans at weight 500: a little
 *  over the real width, so a word estimated to fit does fit. */
function charEm(ch: string): number {
  if (/[il|.,:;'!]/.test(ch) || ch === "I") return 0.26;
  if (/[fjrt]/.test(ch)) return 0.36;
  if (/[mw]/.test(ch)) return 0.8;
  if (/[MW]/.test(ch)) return 0.9;
  if (/[A-Z]/.test(ch)) return 0.66;
  if (/[a-z]/.test(ch)) return 0.52;
  if (/[0-9]/.test(ch)) return 0.58;
  return 0.45;
}

function wordPx(word: string, fontPx: number, trackingEm: number): number {
  let em = 0;
  for (const ch of word) em += charEm(ch) + trackingEm;
  return em * fontPx;
}

/** The row head's side pads (4 px each) and a few px of slack. */
const ROW_HEAD_SPARE_PX = 12;
/** Past this a ward name may still break: the cells keep most of the row. */
const ROW_HEAD_MAX_PX = 112;

/**
 * #3078 — the width of the sheet's fixed row head column: at least `minPx`
 * (44 px upright, 52 px sideways), and wide enough for the longest single
 * word of any row head, so no head breaks inside a word ("CLINI / C").
 * Ward heads are 13 px; block heads (POST CALL, ON CALL, CLINIC, LEAVE, MC)
 * are 11 px capitals with 0.04em tracking (`weekSheet.css`).
 */
export function rowHeadWidthPx(
  { wards, blocks }: { wards: readonly string[]; blocks: readonly string[] },
  minPx: number,
): number {
  let widest = 0;
  for (const label of wards) {
    for (const w of label.split(/\s+/)) widest = Math.max(widest, wordPx(w, 13, 0));
  }
  for (const label of blocks) {
    for (const w of label.toUpperCase().split(/\s+/)) {
      widest = Math.max(widest, wordPx(w, 11, 0.04));
    }
  }
  return Math.min(ROW_HEAD_MAX_PX, Math.max(minPx, Math.ceil(widest + ROW_HEAD_SPARE_PX)));
}

const SESSION_ORDER: Record<string, number> = { AM: 0, PM: 1 };
const FULL_DAY = new Set(["FD", "FULL", "FULL DAY"]);

function sessionRank(s: string): number {
  return SESSION_ORDER[s.toUpperCase()] ?? 50;
}

/** The sessions the wards run, AM before PM, with the team's labels. */
export function sheetSessions(
  stations: readonly SheetStation[] & readonly { sessions?: readonly string[] | null }[],
  defs: readonly SheetSessionDef[] = [],
): SheetSession[] {
  const keys: string[] = [];
  for (const s of stations) {
    if (!isWardStation(s)) continue;
    for (const k of s.sessions ?? []) {
      if (FULL_DAY.has(k.toUpperCase()) || keys.includes(k)) continue;
      keys.push(k);
    }
  }
  if (keys.length === 0) keys.push("AM", "PM");
  const labelOf = new Map(defs.map((d) => [d.key.toUpperCase(), d.label]));
  return keys
    .map((key, i) => ({ key, i }))
    .sort((a, b) => sessionRank(a.key) - sessionRank(b.key) || a.i - b.i)
    .map(({ key }) => ({ key, label: labelOf.get(key.toUpperCase()) || key }));
}

/** The level columns: the team's levels in order, else one "Names" column. */
export function sheetLevels(tiers: readonly SheetTier[]): SheetLevel[] {
  const out = tiers
    .filter((t) => t.key)
    .map((t) => ({
      key: t.key,
      label: t.label || t.key,
      short: levelShortLabel(t.label || t.key),
    }));
  if (out.length === 0)
    return [{ key: NO_LEVEL_KEY, label: NAMES_COLUMN_LABEL, short: NAMES_COLUMN_LABEL }];
  return out;
}

// ─── Short names (founder Q4 (a)) ─────────────────────────────────────

/**
 * Short name per member id: the stored abbreviation, else the initials the
 * roster page derives for its person chips (`buildDerivedAbbreviations`:
 * "Tan Wei Ming" → "TWM", one word → its first three letters, a clash in
 * the team gets a digit suffix, "KR2"). KKH names put the family name
 * first, so a first-word rule would print family names.
 */
export function shortNames(members: readonly SheetMember[]): Map<string, string> {
  const derived = buildDerivedAbbreviations(members);
  const out = new Map<string, string>();
  for (const m of members) {
    const abbr = m.abbreviation?.trim();
    out.set(m.id, abbr ? abbr : (derived.get(m.display_name) ?? m.display_name));
  }
  return out;
}

// ─── The sheet ────────────────────────────────────────────────────────

function sessionsOfSlot(slotSession: string, sessions: readonly SheetSession[]): string[] {
  const upper = slotSession.toUpperCase();
  if (FULL_DAY.has(upper)) return sessions.map((s) => s.key);
  const hit = sessions.find((s) => s.key.toUpperCase() === upper);
  return hit ? [hit.key] : [];
}

function emptyRow(key: string, label: string, kind: SheetRowKind): SheetRow {
  return { key, label, kind, cells: {} };
}

function put(row: SheetRow, key: string, name: SheetName): void {
  const list = row.cells[key] ?? [];
  if (!list.some((n) => n.memberId === name.memberId && n.post === name.post)) list.push(name);
  row.cells[key] = list;
}

/** The call kinds by rank. A Map, not an object: `in` would also match an
 *  object's built-in keys such as `toString`. */
const CALL_KIND_RANK: ReadonlyMap<string, number> = new Map(
  CONSULTANT_CALL_KINDS.map((k, i) => [k, i]),
);

function sheetConsultant(r: SheetConsultantRow, own: ReadonlySet<string>): SheetConsultant {
  const memberId = r.member_id ?? null;
  return {
    name: r.name,
    tag: r.tag ?? null,
    beds: r.beds ?? null,
    memberId,
    own: memberId !== null && own.has(memberId),
  };
}

/** #3022 — the call consultants on the given dates, by date. Ward rows have
 *  their own line and the weekend round never shows. */
export function callConsultantsByDate(
  rows: readonly SheetConsultantRow[],
  dates: ReadonlySet<string>,
  own: ReadonlySet<string>,
): Record<string, SheetConsultantPost[]> {
  const out: Record<string, SheetConsultantPost[]> = {};
  for (const r of rows) {
    if (!CALL_KIND_RANK.has(r.kind) || !dates.has(r.date)) continue;
    const list = out[r.date] ?? [];
    out[r.date] = list;
    let post = list.find((p) => p.post === r.post);
    if (!post) {
      post = { kind: r.kind as ConsultantCallKind, post: r.post, people: [] };
      list.push(post);
    }
    post.people.push(sheetConsultant(r, own));
  }
  for (const list of Object.values(out))
    list.sort(
      (a, b) =>
        (CALL_KIND_RANK.get(a.kind) ?? 9) - (CALL_KIND_RANK.get(b.kind) ?? 9) ||
        a.post.localeCompare(b.post, undefined, { numeric: true }),
    );
  return out;
}

/** The first number of a bed range ("13-24, 27-32" → 13); a row with no
 *  bed range (a whole ward, or a note such as "HAEM/ONCO") sorts after the
 *  ranges. */
function firstBed(beds: string | null): number {
  const hit = /^\s*(\d+)/.exec(beds ?? "");
  return hit ? Number(hit[1]) : Number.POSITIVE_INFINITY;
}

/** #3022 S8 — the ward consultants on the given dates, by ward (the station
 *  in lower case) and date, in bed number order. Two consultants on one bed
 *  range keep the read's order. A row with no station is not shown. */
export function wardConsultantsByWard(
  rows: readonly SheetConsultantRow[],
  dates: ReadonlySet<string>,
  own: ReadonlySet<string>,
): Map<string, Record<string, SheetConsultant[]>> {
  const out = new Map<string, Record<string, SheetConsultant[]>>();
  for (const r of rows) {
    const station = r.station?.trim();
    if (r.kind !== "ward" || !station || !dates.has(r.date)) continue;
    const key = station.toLocaleLowerCase();
    const byDate = out.get(key) ?? {};
    out.set(key, byDate);
    const list = byDate[r.date] ?? [];
    byDate[r.date] = list;
    list.push(sheetConsultant(r, own));
  }
  for (const byDate of out.values())
    for (const list of Object.values(byDate))
      list.sort((a, b) => {
        const x = firstBed(a.beds);
        const y = firstBed(b.beds);
        return x === y ? 0 : x < y ? -1 : 1;
      });
  return out;
}

export function buildWeekSheet(input: WeekSheetInput): WeekSheet {
  const dates = [...input.dates];
  const dateSet = new Set(dates);
  const stations = input.stations as readonly (SheetStation & {
    sessions?: readonly string[] | null;
  })[];
  const sessions = sheetSessions(stations, input.sessions ?? []);
  const levels = sheetLevels(input.tiers);
  const levelKeys = new Set(levels.map((l) => l.key));
  const noLevels = levels.length === 1 && levels[0]?.key === NO_LEVEL_KEY;
  const own = new Set(input.ownMemberIds ?? []);
  const members = new Map(input.members.map((m) => [m.id, m]));
  const shorts = shortNames(input.members);
  const firstSession = sessions[0]?.key ?? "AM";
  let needsOther = false;

  const levelOf = (memberId: string): string => {
    if (noLevels) return NO_LEVEL_KEY;
    const tier = members.get(memberId)?.tier ?? "";
    if (levelKeys.has(tier)) return tier;
    needsOther = true;
    return OTHER_LEVEL_KEY;
  };
  const nameOf = (memberId: string, draft: boolean, post: string | null): SheetName | null => {
    const m = members.get(memberId);
    if (!m) return null;
    return {
      memberId,
      fullName: m.display_name,
      shortName: shorts.get(memberId) ?? m.display_name,
      own: own.has(memberId),
      draft,
      post,
    };
  };

  const seats = input.assignments.filter((a) => input.includeDrafts || a.is_published !== false);

  // Ward rows in groups, plus any station a seat names that the config lost.
  const extra: string[] = [];
  for (const a of seats) {
    if (isOnCall(a)) continue;
    const { station } = splitSlot(a.slot_id);
    if (!extra.includes(station)) extra.push(station);
  }
  const { flat, groups: wardGroupList } = wardGroups(stations, extra);
  const rowByWard = new Map<string, SheetRow>();
  const groups: SheetRowGroup[] = wardGroupList.map((g) => ({
    key: g.key,
    letter: flat ? null : g.letter,
    title: g.title,
    headLine: groupHeadLine(g),
    rows: g.wards.map((w) => {
      const row = emptyRow(`ward:${w}`, w, "ward");
      rowByWard.set(w.toLocaleLowerCase(), row);
      return row;
    }),
  }));

  const postCall = emptyRow("post_call", POST_CALL_LABEL, "post_call");
  const onCall = emptyRow("on_call", ON_CALL_LABEL, "on_call");
  const hasClinic = stations.some((s) => s.enabled !== false && isClinicStation(s.name));
  const clinic = emptyRow("clinic", CLINIC_LABEL, "clinic");
  const leave = emptyRow("leave", LEAVE_LABEL, "leave");
  const mc = emptyRow("mc", MC_LABEL, "mc");
  const lead = input.away !== undefined;
  let hasDrafts = false;

  // Who holds a ward seat in the first session of a day (POST CALL rule).
  const seatedMorning = new Set<string>();
  const calls: { memberId: string; date: string; draft: boolean }[] = [];

  for (const a of seats) {
    const draft = a.is_published === false;
    if (isOnCall(a)) {
      calls.push({ memberId: a.member_id, date: a.date, draft });
      if (!dateSet.has(a.date)) continue;
      const name = nameOf(a.member_id, draft, callPostOf(a.slot_id) || null);
      if (!name) continue;
      put(onCall, cellKey(a.date, firstSession, levelOf(a.member_id)), name);
      if (draft) hasDrafts = true;
      continue;
    }
    if (a.absent === "mc") {
      if (lead && dateSet.has(a.date)) {
        const name = nameOf(a.member_id, false, null);
        if (name)
          for (const s of sessions) put(mc, cellKey(a.date, s.key, levelOf(a.member_id)), name);
      }
      continue;
    }
    const { station, session } = splitSlot(a.slot_id);
    const slotSessions = sessionsOfSlot(session, sessions);
    const row = isClinicStation(station) ? clinic : rowByWard.get(station.toLocaleLowerCase());
    if (row?.kind === "ward" && slotSessions.includes(firstSession))
      seatedMorning.add(`${a.member_id}|${a.date}`);
    if (!row || !dateSet.has(a.date)) continue;
    const name = nameOf(a.member_id, draft, null);
    if (!name) continue;
    for (const s of slotSessions) put(row, cellKey(a.date, s, levelOf(a.member_id)), name);
    if (draft) hasDrafts = true;
  }

  for (const c of calls) {
    const next = addDays(c.date, 1);
    if (!dateSet.has(next) || seatedMorning.has(`${c.memberId}|${next}`)) continue;
    const name = nameOf(c.memberId, c.draft, null);
    if (name) put(postCall, cellKey(next, firstSession, levelOf(c.memberId)), name);
  }

  for (const w of input.away ?? []) {
    const row = w.kind === "mc" ? mc : leave;
    const name = nameOf(w.memberId, false, null);
    if (!name) continue;
    for (const d of dates) {
      if (d < w.from || d > w.to) continue;
      for (const s of sessions) put(row, cellKey(d, s.key, levelOf(w.memberId)), name);
    }
  }

  const byName = (a: SheetName, b: SheetName) => a.fullName.localeCompare(b.fullName);
  const byPost = (a: SheetName, b: SheetName) =>
    (a.post ?? "").localeCompare(b.post ?? "", undefined, { numeric: true }) || byName(a, b);
  for (const g of groups)
    for (const r of g.rows) for (const l of Object.values(r.cells)) l.sort(byName);
  for (const r of [postCall, clinic, leave, mc])
    for (const l of Object.values(r.cells)) l.sort(byName);
  for (const l of Object.values(onCall.cells)) l.sort(byPost);

  const outLevels = needsOther
    ? [...levels, { key: OTHER_LEVEL_KEY, label: OTHER_LEVEL_LABEL, short: OTHER_LEVEL_LABEL }]
    : levels;
  const blockRows = [postCall, onCall];
  if (hasClinic) blockRows.push(clinic);
  if (lead) blockRows.push(leave, mc);

  const callConsultants = callConsultantsByDate(input.consultants ?? [], dateSet, own);
  const wardConsultants = wardConsultantsByWard(input.consultants ?? [], dateSet, own);

  return {
    dates,
    sessions,
    levels: outLevels,
    flat,
    groups,
    blockRows,
    hasDrafts,
    callConsultants,
    wardConsultants,
  };
}
